import { useMemo, useState } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { Plus, Save } from 'lucide-react';
import { Button, Checkbox, DataTable, DateField, FormField, FormGrid, Modal, NumberField, PageHeader, Select, StatusBadge, SubTableEditor, TextField, Textarea, useToast, type Column } from '@/components';
import { errorMessage } from '@/lib/api';
import { applyApiErrors } from '@/lib/form';
import { useBusinessOperators, useCodeValues, useSaveMaster, type BusinessOperatorDto } from '../api';
import { CellError, NumCell } from './cells';
import { MasterEditor } from './MasterEditor';

type Err = { message?: string } | undefined;
const msg = (e: unknown): string | undefined => (e as Err)?.message;
const nz = (v: string | null | undefined) => v ?? '';

// =============================================================== 事業実施者
const CSV_FORMATS = [
  { value: '', label: '取込不可' },
  { value: 'kunigami', label: '国頭形式' },
  { value: 'standard', label: '標準形式' },
];

const operatorSchema = z.object({
  id: z.number({ invalid_type_error: '事業実施者IDを入力してください', required_error: '事業実施者IDを入力してください' }).int().min(1, '1以上の整数で入力してください').max(999999, '6桁以内で入力してください').nullable(),
  name: z.string().trim().min(1, '事業実施者名を入力してください').max(100, '100文字以内で入力してください'),
  formalName: z.string().trim().max(150, '150文字以内で入力してください'),
  catchCsvFormat: z.string(),
  isActive: z.boolean(),
  note: z.string().max(1000),
});
type OperatorValues = z.infer<typeof operatorSchema>;

/** 事業実施者マスタ（年度に依存しない）。一覧＋モーダルで登録・更新 */
export function OperatorsEditor() {
  const list = useBusinessOperators(undefined, { includeInactive: true });
  const [editing, setEditing] = useState<BusinessOperatorDto | 'new' | null>(null);
  const columns: Column<BusinessOperatorDto>[] = [
    { key: 'id', header: '事業実施者ID', numeric: true, sortable: true, width: 120 },
    { key: 'name', header: '事業実施者名', sortable: true, render: (o) => <strong>{o.name}</strong> },
    { key: 'formalName', header: '正式名称', render: (o) => o.formalName ?? <span style={{ color: 'var(--text-muted)' }} title="正式名称が未設定のため、事業実施者名から作る名称を帳票に使います">{o.name.replace(/漁協$/, '漁業協同組合')}（名称と同じ）</span> },
    { key: 'catchCsvFormat', header: '漁獲CSV取込形式', width: 150, render: (o) => CSV_FORMATS.find((f) => f.value === (o.catchCsvFormat ?? ''))?.label ?? o.catchCsvFormat },
    { key: 'isActive', header: '状態', width: 90, align: 'center', render: (o) => (o.isActive ? <StatusBadge tone="success" dot>有効</StatusBadge> : <StatusBadge dot>廃止</StatusBadge>) },
  ];
  return (
    <>
      <PageHeader
        eyebrow="システム管理 ／ マスタ管理"
        title="事業実施者"
        description="事業実施者（漁協等）のマスタです。ID は kintone の事業実施者ID と同じ番号を使います。廃止した事業実施者は選択肢に出ません。"
        actions={<Button variant="primary" icon={<Plus size={16} />} onClick={() => setEditing('new')}>新規登録</Button>}
      />
      <DataTable caption="事業実施者一覧" columns={columns} rows={list.data ?? []} rowKey={(o) => o.id} loading={list.isPending} dense defaultSort={{ key: 'id', direction: 'asc' }} pagination={{ pageSize: 20 }} onRowClick={(o) => setEditing(o)} rowClassName={(o) => (o.isActive ? undefined : 'is-muted')} />
      {editing && <OperatorDialog key={editing === 'new' ? 'new' : editing.id} target={editing === 'new' ? undefined : editing} onClose={() => setEditing(null)} />}
    </>
  );
}

function OperatorDialog({ target, onClose }: { target?: BusinessOperatorDto; onClose: () => void }) {
  const isNew = !target;
  const toast = useToast();
  const save = useSaveMaster<unknown>(isNew ? '/masters/business-operators' : `/masters/business-operators/${target.id}`, isNew ? 'post' : 'put');
  const {
    register,
    control,
    handleSubmit,
    setError,
    formState: { errors, isSubmitting },
  } = useForm<OperatorValues>({
    resolver: zodResolver(operatorSchema),
    defaultValues: { id: target?.id ?? null, name: target?.name ?? '', formalName: nz(target?.formalName), catchCsvFormat: nz(target?.catchCsvFormat), isActive: target?.isActive ?? true, note: nz(target?.note) },
  });
  const onSubmit = handleSubmit(async (v) => {
    try {
      await save.mutateAsync({ id: v.id, name: v.name, formalName: v.formalName || null, catchCsvFormat: v.catchCsvFormat || null, isActive: v.isActive, note: v.note || null });
      toast.success(isNew ? '事業実施者を登録しました' : '事業実施者を更新しました', v.name);
      onClose();
    } catch (e) {
      if (!applyApiErrors(e, setError)) toast.error('保存できませんでした', errorMessage(e));
    }
  });
  return (
    <Modal
      open
      onClose={onClose}
      size="md"
      title={isNew ? '事業実施者の新規登録' : '事業実施者の編集'}
      footer={
        <>
          <Button onClick={onClose}>キャンセル</Button>
          <Button variant="primary" icon={<Save size={16} />} onClick={() => void onSubmit()} loading={isSubmitting}>{isNew ? '登録する' : '保存する'}</Button>
        </>
      }
    >
      <form onSubmit={onSubmit} noValidate>
        <FormGrid columns={2}>
          <FormField label="事業実施者ID" required error={errors.id?.message} hint={isNew ? '例：1000' : '登録後は変更できません'}>
            <Controller control={control} name="id" render={({ field }) => <NumberField {...field} width="sm" disabled={!isNew} invalid={!!errors.id} />} />
          </FormField>
          <FormField label="事業実施者名" required error={errors.name?.message}>
            <TextField data-autofocus {...register('name')} />
          </FormField>
          <FormField label="正式名称" span={2} hint="帳票の請求先表記。空欄の場合は事業実施者名を帳票の表記に使います（末尾が「漁協」の場合は「漁業協同組合」に読み替え）" error={errors.formalName?.message}>
            <TextField {...register('formalName')} />
          </FormField>
          <FormField label="漁獲CSV取込形式">
            <Select options={CSV_FORMATS} {...register('catchCsvFormat')} />
          </FormField>
          <FormField label="状態">
            <Controller control={control} name="isActive" render={({ field }) => <Checkbox label="有効（廃止すると選択肢に出ません）" checked={field.value} onChange={(e) => field.onChange(e.target.checked)} />} />
          </FormField>
          <FormField label="備考" span={2}>
            <Textarea rows={2} {...register('note')} />
          </FormField>
        </FormGrid>
      </form>
    </Modal>
  );
}

// =============================================================== 期間別税率
interface RateForm {
  items: { rateType: string; startDate: string; endDate: string; rate: number | null; note: string }[];
}
export function RatePeriodsEditor() {
  const { data: codes } = useCodeValues();
  return (
    <MasterEditor<RateForm, { items: { rateType: string; startDate: string; endDate: string; rate: number; note: string | null }[] }>
      title="期間別税率マスタ"
      description="消費税・インボイス経過措置控除割合・軽油引取税・潤滑油係数を期間で管理します。判定日（原則 調査期間TO＝帰港日）が適用期間内の行を採用します。同じ種別で期間が重なる行は保存できません。"
      path="/masters/rate-periods"
      savedMessage="期間別税率を保存しました"
      toForm={(d) => ({ items: d.items.map((i) => ({ ...i, note: nz(i.note) })) })}
      toBody={(v) => ({ items: v.items.map((i) => ({ rateType: i.rateType, startDate: i.startDate, endDate: i.endDate, rate: i.rate ?? 0, note: i.note || null })) })}
    >
      {({ control, register, formState: { errors } }) => (
        <SubTableEditor
          control={control}
          name="items"
          title="期間別税率"
          createRow={() => ({ rateType: '消費税', startDate: '', endDate: '', rate: null, note: '' })}
          columns={[
            { key: 'type', header: '種別', width: 230, required: true, render: ({ path }) => <Select aria-label="種別" options={(codes?.rateTypes ?? []).map((v) => ({ value: v, label: v }))} {...register(`${path}.rateType`)} /> },
            { key: 'from', header: '適用開始日', width: 160, required: true, render: ({ path, index }) => <><DateField aria-label="適用開始日" invalid={!!errors.items?.[index]?.startDate} {...register(`${path}.startDate`)} /><CellError message={msg(errors.items?.[index]?.startDate)} /></> },
            { key: 'to', header: '適用終了日', width: 160, required: true, render: ({ path, index }) => <><DateField aria-label="適用終了日" invalid={!!errors.items?.[index]?.endDate} {...register(`${path}.endDate`)} /><CellError message={msg(errors.items?.[index]?.endDate)} /></> },
            { key: 'rate', header: '率・係数・単価', width: 150, align: 'right', required: true, render: ({ path, index }) => <NumCell control={control} name={`${path}.rate`} label="率" decimals={4} error={msg(errors.items?.[index]?.rate)} /> },
            { key: 'note', header: '備考', render: ({ path }) => <TextField aria-label="備考" {...register(`${path}.note`)} /> },
          ]}
        />
      )}
    </MasterEditor>
  );
}

// =============================================================== 魚種・類型
interface FishForm {
  types: { code: string; name: string; note: string }[];
  species: { code: string; standardName: string; localName: string; typeCode: string; note: string; displayOrder: number | null }[];
}
export function FishSpeciesEditor() {
  return (
    <MasterEditor<FishForm, { types: { code: string; name: string; note: string | null }[]; species: { code: string; standardName: string; localName: string | null; typeCode: string | null; note: string | null; displayOrder: number }[] }>
      title="魚種・類型マスタ"
      description="漁獲CSV取込・漁場情報登録で使う魚種と類型です。魚種の類型CDは、上の類型一覧に登録した類型CDを指定します。"
      path="/masters/fish-species"
      savedMessage="魚種・類型を保存しました"
      toForm={(d) => ({
        types: d.types.map((t) => ({ ...t, note: nz(t.note) })),
        species: d.species.map((s) => ({ code: s.code, standardName: s.standardName, localName: nz(s.localName), typeCode: nz(s.typeCode), note: nz(s.note), displayOrder: s.displayOrder })),
      })}
      toBody={(v) => ({
        types: v.types.map((t) => ({ code: t.code, name: t.name, note: t.note || null })),
        species: v.species.map((s) => ({ code: s.code, standardName: s.standardName, localName: s.localName || null, typeCode: s.typeCode || null, note: s.note || null, displayOrder: s.displayOrder ?? 0 })),
      })}
    >
      {({ control, register, watch, formState: { errors } }) => {
        const types = watch('types');
        return (
          <div style={{ display: 'grid', gap: 20 }}>
            <SubTableEditor
              control={control}
              name="types"
              title="魚種類型"
              createRow={() => ({ code: '', name: '', note: '' })}
              columns={[
                { key: 'code', header: '類型CD', width: 110, required: true, render: ({ path, index }) => <><TextField aria-label="類型CD" maxLength={4} invalid={!!errors.types?.[index]?.code} {...register(`${path}.code`)} /><CellError message={msg(errors.types?.[index]?.code)} /></> },
                { key: 'name', header: '類型名', required: true, render: ({ path }) => <TextField aria-label="類型名" maxLength={50} {...register(`${path}.name`)} /> },
                { key: 'note', header: '備考', render: ({ path }) => <TextField aria-label="備考" {...register(`${path}.note`)} /> },
              ]}
            />
            <SubTableEditor
              control={control}
              name="species"
              title="魚種"
              reorderable={false}
              createRow={() => ({ code: '', standardName: '', localName: '', typeCode: '', note: '', displayOrder: 0 })}
              columns={[
                { key: 'code', header: '魚種CD', width: 110, required: true, render: ({ path, index }) => <><TextField aria-label="魚種CD" maxLength={8} invalid={!!errors.species?.[index]?.code} {...register(`${path}.code`)} /><CellError message={msg(errors.species?.[index]?.code)} /></> },
                { key: 'std', header: '標準和名', required: true, render: ({ path }) => <TextField aria-label="標準和名" maxLength={100} {...register(`${path}.standardName`)} /> },
                { key: 'local', header: '地方名', render: ({ path }) => <TextField aria-label="地方名" maxLength={100} {...register(`${path}.localName`)} /> },
                { key: 'type', header: '類型', width: 180, render: ({ path, index }) => <><Select aria-label="類型" placeholder="なし" invalid={!!errors.species?.[index]?.typeCode} options={types.filter((t) => t.code).map((t) => ({ value: t.code, label: `${t.code} ${t.name}` }))} {...register(`${path}.typeCode`)} /><CellError message={msg(errors.species?.[index]?.typeCode)} /></> },
                { key: 'order', header: '表示順', width: 100, align: 'right', render: ({ path }) => <NumCell control={control} name={`${path}.displayOrder`} label="表示順" /> },
              ]}
            />
          </div>
        );
      }}
    </MasterEditor>
  );
}

// =============================================================== 海域図セル
interface GridResp {
  imageUrl: string;
  rows: number;
  cols: number;
  cells: { rowNo: number; colNo: number; zoneNo: number }[];
}
interface GridForm {
  rows: number;
  cols: number;
  grid: string[][];
}
export function SeaAreaGridEditor() {
  return (
    <MasterEditor<GridForm, GridResp>
      title="海域図セル"
      description="海域図（18行×30列）の各セルに表示する農林漁区番号です。海域図の背景画像（120°E〜135°E・21°N〜30°N、30′格子）の左上を 1 行 1 列として入力します。空欄のセルは表示しません。"
      path="/masters/sea-area-grid"
      savedMessage="海域図セルを保存しました"
      toForm={(d) => {
        const grid = Array.from({ length: d.rows }, () => Array.from({ length: d.cols }, () => ''));
        d.cells.forEach((c) => {
          const row = grid[c.rowNo];
          if (row) row[c.colNo] = String(c.zoneNo);
        });
        return { rows: d.rows, cols: d.cols, grid };
      }}
      toBody={(v) => ({
        cells: v.grid.flatMap((row, rowNo) => row.map((val, colNo) => ({ rowNo, colNo, zoneNo: Number(val.normalize('NFKC')) })).filter((c, i) => row[i]!.trim() !== '' && Number.isFinite(c.zoneNo))),
      })}
    >
      {({ register, getValues }) => <GridInputs register={register} rows={getValues('rows')} cols={getValues('cols')} />}
    </MasterEditor>
  );
}

function GridInputs({ register, rows, cols }: { register: ReturnType<typeof useForm<GridForm>>['register']; rows: number; cols: number }) {
  const colHeads = useMemo(() => Array.from({ length: cols }, (_, i) => i + 1), [cols]);
  return (
    <div style={{ overflowX: 'auto' }}>
      <table className="bs-sea-grid">
        <thead>
          <tr>
            <th />
            {colHeads.map((c) => (
              <th key={c} scope="col">{c}</th>
            ))}
          </tr>
        </thead>
        <tbody>
          {Array.from({ length: rows }, (_, r) => (
            <tr key={r}>
              <th scope="row">{r + 1}</th>
              {colHeads.map((_c, c) => (
                <td key={c}>
                  <input inputMode="numeric" aria-label={`${r + 1}行${c + 1}列`} {...register(`grid.${r}.${c}` as `grid.${number}.${number}`)} />
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}
